import React, { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  OutlinedInput,
  Grid,
  Fade,
  useTheme,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  deleteFieldOfSelectedFile,
  setSelectedField,
  setSelectedFieldDrawer,
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
} from "redux/appSlice";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import strings from "global/constants/StringConstants";
import editorTokens from "../SignRequestEditor.tokens";
import { PlusIcon, PropertiesTabIcon } from "./EditorIcons";
import ValidationsIcon from "assets/icons/Validations.svg";
import FieldDataIcon from "assets/icons/FieldData.svg";
import FormattingIcon from "assets/icons/Formatting.svg";
import { focusRing } from "theme/colors";
import { signerFieldValidationPatterns } from "global/constants/ConstantObject";
import { CustomInput } from "global/components";
import CustomSwitch from "global/components/CustomSwitch/CustomSwitch";
import { capitalizeText, isTruthy } from "helpers/methods";
import {
  ANY_SIGNER,
  ANY_SIGNER_COLOR,
  ANY_SIGNER_FIELD_TYPES,
  FIELD_LENGTH_LIMIT_MESSAGE,
  MAX_FIELD_LENGTH,
  PATTERN_FIELD_TYPES,
  blockNonLengthKeys,
  customFieldDefaults,
  toLengthDigits,
  FieldsProperties,
  getFieldSize,
  getFieldSizeMobile,
  getNextRadioGroupId,
} from "../SignRequestHelper";
import {
  CustomField,
  FieldListItem,
  FileField,
  RequestParticipant,
  RequestFile,
} from "models/interfaces";
import moment from "moment";
import FieldCustomAccordion from "global/components/CustomAccordion/FieldsCustomAccordion";
import CustomEmptyState from "global/components/CustomEmptyState/CustomEmptyState";
import { selectDefaultDateFormat } from "redux/preferencesSlice";

// Valid field types (normalized with underscores)
const selectFocusSx = {
  "& .MuiSelect-select:focus-visible": {
    outline: `2px solid ${focusRing}`,
    outlineOffset: "-2px",
  },
};

const VALID_FIELD_TYPES = [
  "signature",
  "name",
  "email",
  "initials",
  "text",
  "date_signed",
  "date",
  "checkbox",
  "radio_button",
  "button",
  "drop_down",
  "company_name",
  "title",
  "attachment",
];

/** The "Assigned to" entry for fields any signer may fill (signerIndex -1). */
const ANY_SIGNER_OPTION: Partial<RequestParticipant> = {
  id: ANY_SIGNER,
  name: strings.anySigner,
  color: ANY_SIGNER_COLOR,
};

/** Select value that switches the pattern list to a sender-typed regex. */
const CUSTOM_PATTERN_VALUE = "__custom__";

const DATE_FORMAT_OPTIONS = [
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD MMM YYYY",
  "MMM DD, YYYY",
];

// Map normalized types to strings constants format (for getFieldSize compatibility)
const TYPE_TO_STRINGS_MAP: Record<string, string> = {
  date_signed: "date signed",
  company_name: "company name",
  radio_button: "radio_button",
  drop_down: "drop_down",
};

const NORMALIZED_TYPE_TO_UI_MAP: Record<string, string> = {
  date_signed: strings.dateSigned,
  company_name: strings.companyName,
};

// Normalize field type: extract type from formats like "Yvette-date_signed-1" or validate existing type
const validateAndNormalizeFieldType = (value: string): string => {
  if (!value || typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();

  // Check if value matches pattern: "signer-fieldtype-count"
  // Examples: "Yvette-date_signed-1", "Yvette-signature-1", "Yvette-company_name-1", "Yvette-text-1"
  // Pattern: starts with text (signer name), then dash, then field type (single word or words with underscores/dashes), then optional dash and number
  const patternMatch = trimmedValue.match(
    /^[^-]+-([a-z_]+(?:[_-][a-z_]+)*)-?\d*$/i,
  );
  if (patternMatch) {
    const extractedType = patternMatch[1].toLowerCase();
    // Normalize extracted type (handle both "date signed" and "date_signed", "company name" and "company_name")
    // Also handle single words like "signature", "text", "name", etc.
    const normalizedType = extractedType
      .replace(/\s+/g, "_")
      .replace(/-/g, "_");

    // Check if normalized type is valid
    if (VALID_FIELD_TYPES.includes(normalizedType)) {
      return normalizedType;
    }

    // Also check if it matches any valid type when we try different separators
    // Handle cases like "date-signed" -> "date_signed" or single words like "signature"
    for (const validType of VALID_FIELD_TYPES) {
      if (
        normalizedType === validType ||
        normalizedType === validType.replace(/_/g, "-") ||
        normalizedType === validType.replace(/_/g, " ")
      ) {
        return validType;
      }
    }
  }

  // If value is already a valid type (with or without underscores/spaces), normalize it
  const normalizedValue = trimmedValue.replace(/\s+/g, "_").toLowerCase();
  if (VALID_FIELD_TYPES.includes(normalizedValue)) {
    return normalizedValue;
  }

  // If value contains a valid type as substring, try to extract it
  // This handles cases where the type might be embedded in a longer string
  for (const validType of VALID_FIELD_TYPES) {
    const typeWithSpaces = validType.replace(/_/g, " ");
    if (
      trimmedValue.toLowerCase().includes(validType) ||
      trimmedValue.toLowerCase().includes(typeWithSpaces)
    ) {
      return validType;
    }
  }

  // If no valid type found, return original value (will be handled by validation)
  return trimmedValue;
};

const getUiCompatibleFieldType = (value: string): string => {
  const normalizedType = validateAndNormalizeFieldType(value);
  return NORMALIZED_TYPE_TO_UI_MAP[normalizedType] || normalizedType;
};

const PropertiesDrawer = ({
  onClose,
  isMobile,
  headerAction,
}: {
  onClose: () => void;
  isMobile?: boolean;
  headerAction?: React.ReactNode;
}) => {
  const fontSizes = [12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];
  const fontFamilies = ["Times", "Courier", "Helvetica"];
  const colors = [
    "#000000",
    "#0000FF",
    "#FF0000",
    "#008000",
    "#FFA500",
    "#800080",
  ];

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { selectedField, signRequest, isBulkSend, selectedFile, customFields } =
    useAppSelector((state) => state.app);
  const defaultDateFormat = useAppSelector(selectDefaultDateFormat);
  // Library origin of the selected field, if it is still in the library. The
  // field carries its own copy of the config, so this is a hint, not a link.
  const sourceCustomField: CustomField | undefined = selectedField?.customFieldId
    ? ((customFields ?? []) as CustomField[]).find(
        (field) => field.id === selectedField.customFieldId,
      )
    : undefined;
  const selectedFieldType = getUiCompatibleFieldType(selectedField?.type || "");
  // Only a field placed from the Custom Fields section shows the custom-field options
  // (default value, min length, custom pattern, any signer). A field dragged from the
  // Fields panel keeps exactly the properties it had before this feature.
  const isLibraryPlacedField = !!selectedField?.customFieldId;
  /** "I'm the only signer": the sender fills the fields themselves, so there is no default. */
  const isSelfSignRequest = signRequest?.selfSigned === true;
  const [signerOfSelectedField, setSignerOfSelectedField] = useState<
    Partial<RequestParticipant>
  >({ id: 1 });

  const [currentAccordion, setCurrentAccordion] = useState("");
  const [maxLengthError, setMaxLengthError] = useState("");

  const handleAccordion =
    (currentAccordion: string) =>
    (event: React.SyntheticEvent, expanded: boolean) => {
      setCurrentAccordion(expanded ? currentAccordion : "");
    };
  useEffect(() => {
    if (selectedField.signerIndex === ANY_SIGNER) {
      setSignerOfSelectedField(ANY_SIGNER_OPTION);
      return;
    }
    setSignerOfSelectedField(
      signRequest.signers.find(
        (signer: RequestParticipant) => signer.id === selectedField.signerIndex,
      ) ?? { id: selectedField.signerIndex },
    );
  }, [selectedField]);

  const reAssignSigner = (signer: Partial<RequestParticipant>) => {
    if (!signer) return;
    const propsToUpdateField: any = {
      id: selectedField.id,
      signerIndex: signer.id,
      color: signer.color,
    };

    const typeForLabel = selectedField.type || "";
    if (typeForLabel !== strings.radioButton) {
      // Any-signer fields are labelled "Any" so the name still reads as a
      // signer-type-count triple like the others.
      const signerName =
        signer.id === ANY_SIGNER ? "Any" : signer.name || "";
      const firstName = signerName.includes(" ")
        ? signerName.split(" ")[0]
        : signerName;

      if (firstName) {
        const sameTypeCount = selectedFile.fields.filter(
          (f: any) =>
            f.type === typeForLabel &&
            f.signerIndex === signer.id &&
            f.id !== selectedField.id,
        ).length;
        propsToUpdateField.label = `${firstName}-${typeForLabel}-${sameTypeCount + 1}`;
      }
    }

    if (typeForLabel === strings.email) {
      propsToUpdateField.value = signer.email || "";
    }
    // A fresh assignment always starts unclaimed.
    propsToUpdateField.filledBy = "";

    dispatch(updateFieldOfSelectedFile(propsToUpdateField));
    dispatch(updateFieldInSignRequest(propsToUpdateField));
    dispatch(setSelectedField(propsToUpdateField));
  };

  const updateSelectedField = (event: any) => {
    const { name, value } = event.target;
    let newValue: any =
      name === "type" ? getUiCompatibleFieldType(value) : value;

    if (name === "maxLength") {
      const digits = toLengthDigits(value);
      if (digits === "") {
        setMaxLengthError("Max length is required.");
        // Keep field editable when user clears the input (deleting last digit).
        // Stored as 0 so the controlled input can render empty via `|| ""`.
        newValue = 0;
      } else {
        const numericValue = Number(digits);
        const currentValueLength = String(selectedField.value || "").length;

        if (numericValue <= 0) {
          setMaxLengthError("Please enter a valid max length.");
          return;
        }

        // Kept as typed (not clamped) so the sender sees the number they entered beside the
        // reason it's refused; the editor's step validation stops it from being sent.
        if (numericValue > MAX_FIELD_LENGTH) {
          setMaxLengthError(FIELD_LENGTH_LIMIT_MESSAGE);
        } else if (numericValue < currentValueLength) {
          setMaxLengthError(
            `Max length cannot be less than current content length (${currentValueLength}).`,
          );
        } else {
          setMaxLengthError("");
        }
        newValue = numericValue;
      }
    }

    if (name === "minLength") {
      newValue = Number(toLengthDigits(value)) || 0;
    }

    let field: Partial<FileField> = { id: selectedField.id, [name]: newValue };

    // Default Value and the value on the document are ONE value for a library field. `value` is what
    // the signer actually receives; the server copies the default into it only while it is still
    // empty. Without this mirror, correcting the default of an ALREADY SENT envelope through Edit
    // would leave the old value in place and the correction would be silently dropped.
    if (name === "defaultValue" && isLibraryPlacedField) {
      (field as any).value = newValue;
    }

    if (
      selectedFieldType === strings.radioButton &&
      selectedField.groupId &&
      (name === "readOnly" || name === "required")
    ) {
      selectedFile.fields.forEach((f: FileField) => {
        if (f.groupId === selectedField.groupId && f.id !== selectedField.id) {
          const update = { id: f.id, [name]: newValue };
          dispatch(updateFieldOfSelectedFile(update));
          dispatch(updateFieldInSignRequest(update));
        }
      });
    }

    if (event.target.name === "type") {
      // Map normalized type to strings constants format for getFieldSize
      // getFieldSize expects types like "date signed" (with space) or "company name" (with space)
      const $page = document.querySelector(
        `.react-pdf__Page[data-page-number="${(selectedField.page ?? 0) + 1}"]`,
      ) as HTMLElement;
      const pageNumber = parseInt(
        $page.getAttribute("data-page-number") ?? "1",
        10,
      );

      const pageWidth = $page
        ? $page.getBoundingClientRect().width
        : selectedField.frameX;

      const pageHeight = $page
        ? $page.getBoundingClientRect().height
        : selectedField.frameY;
      const typeForSize = TYPE_TO_STRINGS_MAP[newValue] || newValue;
      let defaultValue =
        newValue === strings.checkbox || newValue === strings.radioButton
          ? "false"
          : "";
      if (newValue === strings.email && signerOfSelectedField?.email) {
        defaultValue = signerOfSelectedField.email;
      }
      if (isMobile) {
        const [fieldW, fieldH] = getFieldSizeMobile(typeForSize);

        const fieldPageWidth = (fieldW / pageWidth) * 100;
        const fieldPageHeight = (fieldH / pageHeight) * 100;

        field = {
          ...field,
          value: defaultValue,
          w: fieldPageWidth,
          h: fieldPageHeight,
          fieldPageWidth,
          fieldPageHeight,
          frameX: pageWidth,
          frameY: pageHeight,
        };
      } else {
        const [fieldW, fieldH] = getFieldSize(typeForSize);
        const fieldPageWidth = (fieldW / pageWidth) * 100;
        const fieldPageHeight = (fieldH / pageHeight) * 100;

        field = {
          ...field,
          value: defaultValue,
          w: fieldPageWidth,
          h: fieldPageHeight,
          fieldPageWidth,
          fieldPageHeight,
        };
      }
    }

    if (name === "type") {
      if (newValue === strings.radioButton) {
        // Assign a fresh groupId
        if (
          !selectedField.groupId ||
          selectedField.type !== strings.radioButton
        ) {
          const nextId = getNextRadioGroupId(selectedFile);
          const isDuplicate = selectedFile.fields.some(
            (f: any) =>
              f.type === strings.radioButton &&
              f.id !== selectedField.id &&
              f.groupId === nextId,
          );
          field.groupId = isDuplicate ? `radio_g_${Date.now()}` : nextId;
        }
        const existingRadioCount = selectedFile.fields.filter(
          (f: any) =>
            f.type === strings.radioButton && f.id !== selectedField.id,
        ).length;
        (field as any).label = `Option ${existingRadioCount + 1}`;
        (field as any).dateFormat = null;
        (field as any).readOnly = false;
        (field as any).validationPattern = "none";
        (field as any).validationMessage = "";
        (field as any).placeholder = "";
        (field as any).multiLine = false;
      } else if (newValue === strings.date || newValue === strings.dateSigned) {
        (field as any).dateFormat =
          selectedField.dateFormat || defaultDateFormat;
        (field as any).groupId = null;
        (field as any).readOnly = false;
        (field as any).validationPattern = "none";
        (field as any).validationMessage = "";
        (field as any).placeholder = "";
        (field as any).multiLine = false;
        const signerName = signerOfSelectedField?.name || "";
        const firstName = signerName.includes(" ")
          ? signerName.split(" ")[0]
          : signerName;
        const sameTypeCount = selectedFile.fields.filter(
          (f: any) => f.type === newValue && f.id !== selectedField.id,
        ).length;
        if (firstName)
          (field as any).label =
            `${firstName}-${newValue}-${sameTypeCount + 1}`;
      } else if (newValue === strings.email) {
        (field as any).readOnly = true;
        (field as any).groupId = null;
        (field as any).dateFormat = null;
        const signerName = signerOfSelectedField?.name || "";
        const firstName = signerName.includes(" ")
          ? signerName.split(" ")[0]
          : signerName;
        const sameTypeCount = selectedFile.fields.filter(
          (f: any) => f.type === newValue && f.id !== selectedField.id,
        ).length;
        if (firstName)
          (field as any).label =
            `${firstName}-${newValue}-${sameTypeCount + 1}`;
      } else if (newValue === strings.checkbox) {
        (field as any).groupId = null;
        (field as any).dateFormat = null;
        (field as any).readOnly = false;
        const signerName = signerOfSelectedField?.name || "";
        const firstName = signerName.includes(" ")
          ? signerName.split(" ")[0]
          : signerName;
        const sameTypeCount = selectedFile.fields.filter(
          (f: any) => f.type === newValue && f.id !== selectedField.id,
        ).length;
        if (firstName)
          (field as any).label =
            `${firstName}-${newValue}-${sameTypeCount + 1}`;
      } else {
        (field as any).groupId = null;
        (field as any).dateFormat = null;
        (field as any).readOnly = false;
        const signerName = signerOfSelectedField?.name || "";
        const firstName = signerName.includes(" ")
          ? signerName.split(" ")[0]
          : signerName;
        const sameTypeCount = selectedFile.fields.filter(
          (f: any) => f.type === newValue && f.id !== selectedField.id,
        ).length;
        if (firstName)
          (field as any).label =
            `${firstName}-${newValue}-${sameTypeCount + 1}`;
      }

      // Custom-field settings are per type (dropdown options, pattern), so a
      // type change starts them over.
      Object.assign(field, customFieldDefaults(newValue));
      if (newValue === strings.dropDown || selectedField.customValidation) {
        (field as any).validationPattern = "none";
        (field as any).validationMessage = "";
        (field as any).multiLine = false;
      }
      // Only data fields may stay on "any signer"; anything else goes back to
      // the first signer, the same owner a newly placed field would get.
      if (
        selectedField.signerIndex === ANY_SIGNER &&
        !ANY_SIGNER_FIELD_TYPES.includes(newValue)
      ) {
        const fallback = signRequest.signers.find(
          (signer: RequestParticipant) => !signer.cc,
        );
        if (fallback) {
          (field as any).signerIndex = fallback.id;
          (field as any).color = fallback.color;
        }
      }
    }

    dispatch(updateFieldOfSelectedFile(field));
    dispatch(updateFieldInSignRequest(field));
    if (name === "type") {
      let defaultValue =
        newValue === strings.checkbox || newValue === strings.radioButton
          ? "false"
          : "";
      if (newValue === strings.email && signerOfSelectedField?.email) {
        defaultValue = signerOfSelectedField.email;
      }
      const extraProps: any = {
        groupId: (field as any).groupId ?? null,
        dateFormat: (field as any).dateFormat ?? null,
        readOnly: (field as any).readOnly ?? false,
        label: (field as any).label,
        validationPattern: (field as any).validationPattern,
        validationMessage: (field as any).validationMessage,
        placeholder: (field as any).placeholder,
        multiLine: (field as any).multiLine,
        signerIndex: (field as any).signerIndex,
        color: (field as any).color,
        customValidation: (field as any).customValidation,
        listItems: (field as any).listItems,
        minLength: (field as any).minLength,
        defaultValue: (field as any).defaultValue,
      };
      Object.keys(extraProps).forEach((k) => {
        if (extraProps[k] === undefined) delete extraProps[k];
      });
      dispatch(
        setSelectedField({
          ...selectedField,
          [name]: newValue,
          value: defaultValue,
          ...extraProps,
        }),
      );
    } else {
      // ...field rather than [name]: newValue, so the mirrored `value` above reaches the panel too.
      dispatch(setSelectedField({ ...selectedField, ...field }));
    }
  };

  const updateSelectedFieldStyles = (event: any) => {
    const { name, value } = event.target;
    let h = selectedField.styles.size;
    if (selectedField.h < selectedField.styles.size)
      h = selectedField.styles.size;
    const pdfField = {
      id: selectedField.id,
      h: h,
      styles: { ...selectedField.styles, [name]: value },
    };

    dispatch(updateFieldOfSelectedFile(pdfField));
    dispatch(updateFieldInSignRequest(pdfField));
    dispatch(
      setSelectedField({
        ...selectedField,
        styles: { ...selectedField.styles, h: h, [name]: value },
      }),
    );
  };

  const handleFontWightClick = (name: "bold" | "italic" | "underline") => {
    const pdfField = {
      id: selectedField.id,
      styles: {
        ...selectedField.styles,
        fontWeight: {
          ...selectedField.styles.fontWeight,
          [name]: !selectedField.styles.fontWeight[name],
        },
      },
    };
    dispatch(updateFieldOfSelectedFile(pdfField));
    dispatch(updateFieldInSignRequest(pdfField));
    dispatch(
      setSelectedField({
        ...selectedField,
        styles: {
          ...selectedField.styles,
          fontWeight: {
            ...selectedField.styles.fontWeight,
            [name]: !selectedField.styles.fontWeight[name],
          },
        },
      }),
    );
  };

  const getFieldTypeName = () => {
    return selectedField.type.split("_").join(" ");
  };

  const handleClose = () => {
    dispatch(setSelectedFieldDrawer(false));
    dispatch(setSelectedField(null));
  };

  const handleDelete = () => {
    dispatch(deleteFieldOfSelectedFile(selectedField));
    dispatch(setSelectedFieldDrawer(false));
  };

  const getIcon = (type: string) => {
    switch (type) {
      case strings.formatting:
        return FormattingIcon;
      case strings.fieldData:
        return FieldDataIcon;
      case strings.validations:
        return ValidationsIcon;
    }
  };

  const getAccordionSummary = (type: string, title: string) => {
    return (
      <Grid container display={"flex"} alignItems={"center"}>
        <Grid item>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginX: "7.5px",
              border: `1px solid ${theme.palette.neutral[200]}`,
            }}
          >
            <img
              src={getIcon(type)}
              width={16}
              alt={`${type} field icon`}
              aria-hidden="true"
            />
          </Box>
        </Grid>
        <Grid item xs={9}>
          <Typography variant="body1">{title}</Typography>
        </Grid>
      </Grid>
    );
  };

  const getSummary = (type: string) => {
    switch (type) {
      case strings.formatting:
        return getAccordionSummary(strings.formatting, "Formatting");
      case strings.fieldData:
        return getAccordionSummary(strings.fieldData, "Field Data");
      case strings.validations:
        return getAccordionSummary(strings.validations, "Validations");
    }
  };

  const getDetails = (type: string) => {
    switch (type) {
      case strings.formatting:
        return getFormattingAccordionDetails();
      case strings.fieldData:
        return getFieldDataAccordionDetails();
      case strings.validations:
        return getValidationAccordionDetails();
    }
  };

  /**
   * Switches between a named pattern and a sender-typed regex. A named
   * pattern is looked up by display name on the server; a custom one is used
   * verbatim, so `customValidation` tells the two apart.
   */
  const handleValidationTypeChange = (value: string) => {
    const updates: Partial<FileField> =
      value === CUSTOM_PATTERN_VALUE
        ? { id: selectedField.id, customValidation: true, validationPattern: "" }
        : {
            id: selectedField.id,
            customValidation: false,
            validationPattern: value,
            ...(value === "none" ? { validationMessage: "" } : {}),
          };
    dispatch(updateFieldOfSelectedFile(updates));
    dispatch(updateFieldInSignRequest(updates));
    dispatch(setSelectedField({ ...selectedField, ...updates }));
  };

  const customPatternError = (() => {
    if (!selectedField.customValidation) return "";
    const pattern = selectedField.validationPattern || "";
    if (pattern.trim() === "") return "Enter a regular expression.";
    try {
      new RegExp(pattern);
      return "";
    } catch {
      return "This is not a valid regular expression.";
    }
  })();

  const minLengthError =
    (selectedField.minLength ?? 0) > MAX_FIELD_LENGTH
      ? FIELD_LENGTH_LIMIT_MESSAGE
      : (selectedField.minLength ?? 0) > 0 &&
          (selectedField.maxLength ?? 0) > 0 &&
          (selectedField.minLength ?? 0) > selectedField.maxLength
        ? "Minimum length cannot be greater than max length."
        : "";

  const captionSx = { color: "text.secondary", mb: 0.5, display: "block" };

  const getValidationAccordionDetails = () => {
    const validationSelectValue = selectedField.customValidation
      ? CUSTOM_PATTERN_VALUE
      : selectedField.validationPattern || "none";
    const showMessage =
      selectedField.customValidation ||
      (selectedField.validationPattern &&
        selectedField.validationPattern !== "none");
    return (
      <>
        {selectedFieldType === strings.text && (
          <Box>
            <Typography
              id="validation-type-label"
              variant="caption"
              sx={captionSx}
            >
              Validation Type
            </Typography>
            <Select
              fullWidth
              size="small"
              id="validation-pattern-select"
              labelId="validation-type-label"
              name="validationPattern"
              value={validationSelectValue}
              onChange={(e) =>
                handleValidationTypeChange(e.target.value as string)
              }
              sx={{ fontSize: "0.875rem" }}
            >
              {Object.keys(signerFieldValidationPatterns).map((pattern) => (
                <MenuItem key={pattern} value={pattern}>
                  {pattern}
                </MenuItem>
              ))}
              {isLibraryPlacedField && (
                <MenuItem value={CUSTOM_PATTERN_VALUE}>Custom pattern</MenuItem>
              )}
            </Select>

            {selectedField.customValidation && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={captionSx}>
                  Pattern (regular expression)
                </Typography>
                <CustomInput
                  size="small"
                  id="field-customPattern-input"
                  value={selectedField.validationPattern || ""}
                  name="validationPattern"
                  onChange={updateSelectedField}
                  placeHolder="e.g. ^[A-Z]{2}-[0-9]{4}$"
                  propsToInputElement={{
                    "aria-label": "Validation pattern",
                    ...(isTruthy(customPatternError)
                      ? {
                          "aria-invalid": true,
                          "aria-describedby": "field-customPattern-error",
                        }
                      : {}),
                  }}
                />
                {isTruthy(customPatternError) && (
                  <FormHelperText
                    error
                    id="field-customPattern-error"
                    role="alert"
                  >
                    {customPatternError}
                  </FormHelperText>
                )}
              </Box>
            )}

            {showMessage && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={captionSx}>
                  Validation Message
                </Typography>
                <CustomInput
                  size="small"
                  id="field-validationMessage-input"
                  value={selectedField.validationMessage || ""}
                  name="validationMessage"
                  onChange={updateSelectedField}
                  propsToInputElement={{ "aria-label": "Validation message" }}
                />
              </Box>
            )}

            {/* Text only: a dropdown's value is one of its options, so a length rule could only
                refuse a choice the signer had no way to avoid. */}
            {isLibraryPlacedField && selectedFieldType === strings.text && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={captionSx}>
                Min Length
              </Typography>
              <CustomInput
                size="small"
                type="number"
                id="field-minLength-input"
                value={selectedField.minLength || ""}
                name="minLength"
                onChange={updateSelectedField}
                onKeyDown={blockNonLengthKeys}
                placeHolder="0"
                propsToInputElement={{
                  "aria-label": "Min length",
                  min: 0,
                  max: MAX_FIELD_LENGTH,
                  step: 1,
                  ...(isTruthy(minLengthError)
                    ? {
                        "aria-invalid": true,
                        "aria-describedby": "field-minLength-error",
                      }
                    : {}),
                }}
              />
              {isTruthy(minLengthError) && (
                <FormHelperText error id="field-minLength-error" role="alert">
                  {minLengthError}
                </FormHelperText>
              )}
            </Box>
            )}
          </Box>
        )}

        {selectedFieldType === strings.dropDown && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            The signer can only pick one of the options listed under Field
            Data.
          </Typography>
        )}
      </>
    );
  };

  // ---- dropdown options -------------------------------------------------

  const dropdownOptions: FieldListItem[] = selectedField.listItems ?? [];

  const commitDropdownOptions = (listItems: FieldListItem[]) => {
    const updates: Partial<FileField> = { id: selectedField.id, listItems };
    // Keep the default in step with the options it must belong to.
    if (
      selectedField.defaultValue &&
      !listItems.some((item) => item.text === selectedField.defaultValue)
    ) {
      updates.defaultValue = "";
    }
    dispatch(updateFieldOfSelectedFile(updates));
    dispatch(updateFieldInSignRequest(updates));
    dispatch(setSelectedField({ ...selectedField, ...updates }));
  };

  const addDropdownOption = () => {
    const next = dropdownOptions.length + 1;
    commitDropdownOptions([
      ...dropdownOptions,
      { id: `option_${Date.now()}`, text: `Option ${next}` },
    ]);
  };

  const renameDropdownOption = (index: number, text: string) => {
    commitDropdownOptions(
      dropdownOptions.map((item, i) => (i === index ? { ...item, text } : item)),
    );
  };

  const removeDropdownOption = (index: number) => {
    commitDropdownOptions(dropdownOptions.filter((_, i) => i !== index));
  };

  const duplicateOptionTexts = new Set(
    dropdownOptions
      .map((item) => item.text.trim())
      .filter((text, i, all) => text !== "" && all.indexOf(text) !== i),
  );

  const getDropdownOptionsEditor = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" sx={captionSx}>
        Options
      </Typography>
      <Stack spacing={1}>
        {dropdownOptions.map((item, index) => {
          const duplicate = duplicateOptionTexts.has(item.text.trim());
          return (
            <Stack
              key={item.id}
              direction="row"
              alignItems="center"
              spacing={0.5}
            >
              <CustomInput
                size="small"
                id={`field-option-${index}-input`}
                value={item.text}
                onChange={(e: any) =>
                  renameDropdownOption(index, e.target.value)
                }
                placeHolder={`Option ${index + 1}`}
                propsToInputElement={{
                  "aria-label": `Option ${index + 1}`,
                  ...(duplicate ? { "aria-invalid": true } : {}),
                }}
              />
              <Tooltip title="Remove option" arrow describeChild>
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Remove option ${index + 1}`}
                    disabled={dropdownOptions.length <= 1}
                    onClick={() => removeDropdownOption(index)}
                    sx={{ color: "text.secondary" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>
      {duplicateOptionTexts.size > 0 && (
        <FormHelperText error role="alert">
          Options must be unique.
        </FormHelperText>
      )}
      {dropdownOptions.some((item) => item.text.trim() === "") && (
        <FormHelperText error role="alert">
          Every option needs a label.
        </FormHelperText>
      )}
      <Button
        size="small"
        variant="text"
        onClick={addDropdownOption}
        startIcon={<PlusIcon size={14} />}
        sx={{ mt: 1, textTransform: "none", color: "primary.main" }}
      >
        Add option
      </Button>
    </Box>
  );

  const getDefaultValueInput = () => {
    // In "I'm the only signer" the sender IS the signer, so this box holds the value itself rather
    // than a default for someone else -- and the field on the document writes `value` there too.
    // Binding both to the same property is what keeps them in step.
    const valueKey = isSelfSignRequest ? "value" : "defaultValue";
    const valueCaption = isSelfSignRequest ? "Value" : "Default Value";
    const boundValue = (isSelfSignRequest
      ? selectedField.value
      : selectedField.defaultValue) as string | undefined;
    if (selectedFieldType === strings.dropDown) {
      return (
        <Box>
          <Typography id="default-value-label" variant="caption" sx={captionSx}>
            {valueCaption}
          </Typography>
          <Select
            fullWidth
            size="small"
            labelId="default-value-label"
            name={valueKey}
            displayEmpty
            value={boundValue || ""}
            onChange={updateSelectedField}
            sx={{ fontSize: "0.875rem" }}
          >
            <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
              No default
            </MenuItem>
            {dropdownOptions
              .filter((item) => item.text.trim() !== "")
              .map((item) => (
                <MenuItem
                  key={item.id}
                  value={item.text}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {item.text}
                </MenuItem>
              ))}
          </Select>
        </Box>
      );
    }
    return (
      <Box>
        <Typography variant="caption" sx={captionSx}>
          {valueCaption}
        </Typography>
        <CustomInput
          size="small"
          type="text"
          id="field-defaultValue-input"
          value={boundValue || ""}
          name={valueKey}
          onChange={updateSelectedField}
          placeHolder={
            isSelfSignRequest ? "Enter the value" : "Pre-filled for the signer"
          }
          propsToInputElement={{ "aria-label": valueCaption }}
        />
      </Box>
    );
  };
  const NEW_RADIO_GROUP_VALUE = "__new_radio_group__";

  const handleDateFormatChange = (newFormat: string) => {
    if (!newFormat || newFormat === selectedField.dateFormat) return;
    const oldFormat = selectedField.dateFormat || "MM/DD/YYYY";
    const updates: Partial<FileField> = {
      id: selectedField.id,
      dateFormat: newFormat,
    };
    if (selectedField.value) {
      const parsed = moment(selectedField.value, oldFormat, true);
      if (parsed.isValid()) {
        updates.value = parsed.format(newFormat);
      }
    }
    dispatch(updateFieldOfSelectedFile(updates));
    dispatch(updateFieldInSignRequest(updates));
    dispatch(setSelectedField({ ...selectedField, ...updates }));
  };

  const getRadioGroupOptions = (): {
    id: string;
    label: string;
  }[] => {
    const seen = new Map<string, number>();
    let order = 0;
    selectedFile.fields.forEach((f: FileField) => {
      if (f.type === "radio_button" && f.groupId && !seen.has(f.groupId)) {
        seen.set(f.groupId, order++);
      }
    });
    const letterFor = (idx: number) => {
      let s = "";
      let n = idx;
      do {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      } while (n >= 0);
      return s;
    };
    return Array.from(seen.entries()).map(([id, idx]) => ({
      id,
      label: `Group ${letterFor(idx)}`,
    }));
  };

  const handleRadioGroupChange = (newGroupId: string) => {
    let groupIdToSet = newGroupId;
    if (newGroupId === NEW_RADIO_GROUP_VALUE) {
      groupIdToSet = getNextRadioGroupId(selectedFile);
    }
    if (groupIdToSet === selectedField.groupId) return;
    const updates = {
      id: selectedField.id,
      groupId: groupIdToSet,
      value: "false",
    };
    dispatch(updateFieldOfSelectedFile(updates));
    dispatch(updateFieldInSignRequest(updates));
    dispatch(setSelectedField({ ...selectedField, ...updates }));
  };

  const getFieldDataAccordionDetails = () => {
    return (
      <>
        <CustomInput
          size="small"
          id="field-label-input"
          value={selectedField.label || ""}
          name="label"
          onChange={updateSelectedField}
          placeHolder="Enter field label"
          propsToInputElement={{ "aria-label": "Field label" }}
        />

        {selectedFieldType === strings.radioButton && (
          <Box sx={{ mt: 2 }}>
            <Typography
              id="radio-group-label"
              variant="caption"
              sx={{ color: "text.secondary", mb: 0.5, display: "block" }}
            >
              Group
            </Typography>
            <Select
              fullWidth
              size="small"
              labelId="radio-group-label"
              value={selectedField.groupId || ""}
              onChange={(e) => handleRadioGroupChange(e.target.value as string)}
              sx={{ fontSize: "0.875rem" }}
            >
              {getRadioGroupOptions().map((opt) => (
                <MenuItem
                  key={opt.id}
                  value={opt.id}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {opt.label}
                </MenuItem>
              ))}
              <MenuItem
                value={NEW_RADIO_GROUP_VALUE}
                sx={{
                  fontSize: "0.875rem",
                  color: "primary.main",
                  fontWeight: 500,
                }}
              >
                + New group
              </MenuItem>
            </Select>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
            >
              <Typography variant="body2">Required</Typography>
              <CustomSwitch
                name="required"
                aria-label="Required"
                checked={selectedField.required}
                onChange={(e) =>
                  updateSelectedField({
                    target: { name: "required", value: e.target.checked },
                  })
                }
              />
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
            >
              <Typography variant="body2">Read Only</Typography>
              <CustomSwitch
                name="readOnly"
                aria-label="Read only"
                checked={selectedField.readOnly || false}
                onChange={(e) =>
                  updateSelectedField({
                    target: { name: "readOnly", value: e.target.checked },
                  })
                }
              />
            </Stack>
          </Box>
        )}

        {selectedFieldType === strings.date && (
          <Box sx={{ mt: 2 }}>
            <Typography
              id="date-format-label"
              variant="caption"
              sx={{ color: "text.secondary", mb: 0.5, display: "block" }}
            >
              Date Format
            </Typography>
            <Select
              fullWidth
              size="small"
              name="dateFormat"
              labelId="date-format-label"
              value={selectedField.dateFormat || "MM/DD/YYYY"}
              onChange={(e) => handleDateFormatChange(e.target.value as string)}
              sx={{ fontSize: "0.875rem" }}
            >
              {DATE_FORMAT_OPTIONS.map((fmt) => (
                <MenuItem key={fmt} value={fmt} sx={{ fontSize: "0.875rem" }}>
                  {fmt}
                </MenuItem>
              ))}
            </Select>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
            >
              <Typography variant="body2">Required</Typography>
              <CustomSwitch
                name="required"
                aria-label="Required"
                checked={selectedField.required}
                onChange={(e) =>
                  updateSelectedField({
                    target: { name: "required", value: e.target.checked },
                  })
                }
              />
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
            >
              <Typography variant="body2">Read Only</Typography>
              <CustomSwitch
                name="readOnly"
                aria-label="Read only"
                checked={selectedField.readOnly}
                onChange={(e) =>
                  updateSelectedField({
                    target: { name: "readOnly", value: e.target.checked },
                  })
                }
              />
            </Stack>
          </Box>
        )}

        {selectedFieldType === strings.attachment && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Typography variant="body2">Required</Typography>
            <CustomSwitch
              name="required"
              aria-label="Required"
              checked={selectedField.required}
              onChange={(e) =>
                updateSelectedField({
                  target: { name: "required", value: e.target.checked },
                })
              }
            />
          </Stack>
        )}

        {selectedFieldType === strings.dropDown && getDropdownOptionsEditor()}

        {(selectedFieldType === strings.text ||
          selectedFieldType === strings.dropDown ||
          selectedFieldType === strings.email ||
          selectedFieldType === strings.title ||
          selectedFieldType === strings.companyName ||
          selectedFieldType === strings.dateSigned ||
          selectedFieldType === strings.checkbox ||
          selectedFieldType === strings.name) && (
          <>
            <Stack spacing={1} mt={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Required</Typography>
                <CustomSwitch
                  name="required"
                  aria-label="Required"
                  checked={selectedField.required}
                  onChange={(e) =>
                    updateSelectedField({
                      target: { name: "required", value: e.target.checked },
                    })
                  }
                  // size="small"
                />
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Read Only</Typography>
                <CustomSwitch
                  name="readOnly"
                  aria-label="Read only"
                  checked={selectedField.readOnly}
                  onChange={(e) =>
                    updateSelectedField({
                      target: { name: "readOnly", value: e.target.checked },
                    })
                  }
                  // size="small"
                />
              </Stack>

              {selectedFieldType === strings.text && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">Multiline</Typography>
                  <CustomSwitch
                    name="multiLine"
                    aria-label="Multiline"
                    checked={selectedField.multiLine}
                    onChange={(e) =>
                      updateSelectedField({
                        target: {
                          name: "multiLine",
                          value: e.target.checked,
                        },
                      })
                    }
                    // size="small"
                  />
                </Stack>
              )}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2} mt={2}>
              {isLibraryPlacedField &&
                (selectedFieldType === strings.text ||
                  selectedFieldType === strings.dropDown) &&
                getDefaultValueInput()}
              {selectedFieldType !== strings.email && (
                <>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mb: 0.5,
                        display: "block",
                      }}
                    >
                      Placeholder
                    </Typography>
                    <CustomInput
                      size="small"
                      id="field-placeholder-input"
                      value={selectedField.placeholder || ""}
                      name="placeholder"
                      onChange={updateSelectedField}
                      placeHolder="Enter placeholder text"
                      propsToInputElement={{ "aria-label": "Placeholder" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display:
                        selectedFieldType === strings.dropDown
                          ? "none"
                          : "block",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mb: 0.5,
                        display: "block",
                      }}
                    >
                      Max Length
                    </Typography>
                    <CustomInput
                      size="small"
                      type="number"
                      id="field-maxLength-input"
                      value={selectedField.maxLength || ""}
                      name="maxLength"
                      onChange={updateSelectedField}
                      onKeyDown={blockNonLengthKeys}
                      propsToInputElement={{
                        "aria-label": "Max length",
                        min: 1,
                        max: MAX_FIELD_LENGTH,
                        step: 1,
                        ...(isTruthy(maxLengthError)
                          ? {
                              "aria-invalid": true,
                              "aria-describedby": "field-maxLength-error",
                            }
                          : {}),
                      }}
                    />
                    {isTruthy(maxLengthError) && (
                      <FormHelperText
                        error
                        id="field-maxLength-error"
                        role="alert"
                      >
                        {maxLengthError}
                      </FormHelperText>
                    )}
                  </Box>
                </>
              )}
            </Stack>
          </>
        )}
        {
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mb: 0.5, display: "block" }}
            >
              Tooltip
            </Typography>
            <CustomInput
              size="small"
              id="field-tooltip-input"
              value={selectedField.toolTip || ""}
              name="toolTip"
              onChange={updateSelectedField}
              placeHolder="Enter tooltip text"
              propsToInputElement={{ "aria-label": "Tooltip" }}
            />
          </Box>
        }
      </>
    );
  };
  const getFormattingAccordionDetails = () => {
    return (
      <>
        <Select
          fullWidth
          size="small"
          name="fontFamily"
          inputProps={{ "aria-label": "Font family" }}
          value={selectedField.styles.fontFamily}
          onChange={updateSelectedFieldStyles}
          sx={{ mb: 1, fontSize: "0.875rem", ...selectFocusSx }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
        >
          {fontFamilies.map((font) => (
            <MenuItem
              key={font}
              value={font}
              sx={{ fontFamily: font, fontSize: "0.875rem" }}
            >
              {font}
            </MenuItem>
          ))}
        </Select>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Select
            size="small"
            name="size"
            inputProps={{ "aria-label": "Font size" }}
            value={selectedField.styles.size}
            onChange={updateSelectedFieldStyles}
            sx={{ width: "60px", fontSize: "0.875rem", ...selectFocusSx }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
          >
            {fontSizes.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>

          <IconButton
            size="small"
            aria-label="Bold"
            aria-pressed={selectedField.styles.fontWeight.bold}
            onClick={() => handleFontWightClick("bold")}
            sx={{
              border: `1px solid ${theme.palette.neutral[200]}`,
              borderRadius: 1,
              backgroundColor: selectedField.styles.fontWeight.bold
                ? "#E3F2FD"
                : "transparent",
              "&:hover": {
                backgroundColor: selectedField.styles.fontWeight.bold
                  ? "#BBDEFB"
                  : "neutral.100",
              },
            }}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            aria-label="Italic"
            aria-pressed={selectedField.styles.fontWeight.italic}
            onClick={() => handleFontWightClick("italic")}
            sx={{
              border: `1px solid ${theme.palette.neutral[200]}`,
              borderRadius: 1,
              backgroundColor: selectedField.styles.fontWeight.italic
                ? "#E3F2FD"
                : "transparent",
              "&:hover": {
                backgroundColor: selectedField.styles.fontWeight.italic
                  ? "#BBDEFB"
                  : "neutral.100",
              },
            }}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            aria-label="Underline"
            aria-pressed={selectedField.styles.fontWeight.underline}
            onClick={() => handleFontWightClick("underline")}
            sx={{
              border: `1px solid ${theme.palette.neutral[200]}`,
              borderRadius: 1,
              backgroundColor: selectedField.styles.fontWeight.underline
                ? "#E3F2FD"
                : "transparent",
              "&:hover": {
                backgroundColor: selectedField.styles.fontWeight.underline
                  ? "#BBDEFB"
                  : "neutral.100",
              },
            }}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Typography
            id="field-color-label"
            variant="body2"
            sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}
          >
            Color:
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            role="group"
            aria-labelledby="field-color-label"
          >
            {colors.map((color) => (
              <Box
                mr={0.5}
                key={color}
                role="button"
                tabIndex={0}
                aria-label={`Color ${color}`}
                aria-pressed={selectedField.styles.color === color}
                onClick={() =>
                  updateSelectedFieldStyles({
                    target: { name: "color", value: color },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    updateSelectedFieldStyles({
                      target: { name: "color", value: color },
                    });
                  }
                }}
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: color,
                  border:
                    selectedField.styles.color === color
                      ? "2px solid #2196F3"
                      : `1px solid ${theme.palette.neutral[200]}`,
                  cursor: "pointer",
                  "&:hover": {
                    transform: "scale(1.1)",
                  },
                }}
              />
            ))}
            <Tooltip title="Custom color" arrow>
              <Box sx={{ position: "relative", width: 16, height: 16 }}>
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, red, yellow, green, blue, purple)",
                    cursor: "pointer",
                    border: `1px solid ${theme.palette.neutral[200]}`,
                  }}
                />
                <input
                  type="color"
                  name="color"
                  aria-label="Custom color"
                  value={selectedField.styles.color}
                  onChange={updateSelectedFieldStyles}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                  }}
                />
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </>
    );
  };

  // Check if there are any fields in any documents
  const hasAnyFields = signRequest?.files?.some(
    (file: RequestFile) => file?.fields && file.fields.length > 0,
  );

  // If no fields exist, show empty state with header
  if (!hasAnyFields) {
    return (
      <Fade in={true} timeout={500}>
        <Stack
          direction="column"
          display="flex"
          justifyContent="space-between"
          role="region"
          aria-labelledby="field-properties-heading"
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                ...(isMobile
                  ? { p: 1.5 }
                  : {
                      height: editorTokens.rightRail.headerHeight,
                      px: editorTokens.rightRail.headerPaddingX,
                      flexShrink: 0,
                    }),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: editorTokens.rightRail.headerGap,
                borderBottom: `1px solid ${editorTokens.rightRail.borderColor}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: editorTokens.rightRail.headerGap,
                  color: editorTokens.palette.ink,
                }}
              >
                <PropertiesTabIcon size={18} />
                <Typography
                  id="field-properties-heading"
                  component="h2"
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 800,
                    color: "signer.ink",
                  }}
                >
                  Field properties
                </Typography>
              </Box>
              {headerAction && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {headerAction}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                backgroundColor: "neutral.50",
              }}
            >
              <CustomEmptyState
                title="No Fields Available"
                description="There are no fields in any of the documents. Please add fields to the documents to view and edit their properties."
                bgColor={theme.palette.neutral[50]}
              />
            </Box>
          </Box>
        </Stack>
      </Fade>
    );
  }

  // If selectedField doesn't have a valid ID or type, show message with header
  const hasValidField =
    selectedField?.id !== undefined &&
    selectedField?.id !== null &&
    selectedField?.id !== 0 &&
    selectedField?.type;

  if (!hasValidField) {
    return (
      <Fade in={true} timeout={500}>
        <Stack
          direction="column"
          display="flex"
          justifyContent="space-between"
          role="region"
          aria-labelledby="field-properties-heading"
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                ...(isMobile
                  ? { p: 1.5 }
                  : {
                      height: editorTokens.rightRail.headerHeight,
                      px: editorTokens.rightRail.headerPaddingX,
                      flexShrink: 0,
                    }),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: editorTokens.rightRail.headerGap,
                borderBottom: `1px solid ${editorTokens.rightRail.borderColor}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: editorTokens.rightRail.headerGap,
                  color: editorTokens.palette.ink,
                }}
              >
                <PropertiesTabIcon size={18} />
                <Typography
                  sx={{
                    fontSize: editorTokens.rightRail.headerFontSize,
                    fontWeight: editorTokens.rightRail.headerFontWeight,
                    color: editorTokens.palette.ink,
                    lineHeight: "normal",
                  }}
                >
                  Field properties
                </Typography>
              </Box>
              {headerAction && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {headerAction}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                backgroundColor: "neutral.50",
              }}
            >
              <CustomEmptyState
                title="No Field Selected"
                description="Please select a field from the document to view and edit its properties."
              />
            </Box>
          </Box>
        </Stack>
      </Fade>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <Stack
        direction="column"
        display="flex"
        justifyContent="space-between"
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...(isMobile
                ? { p: 1.5 }
                : {
                    height: editorTokens.rightRail.headerHeight,
                    px: editorTokens.rightRail.headerPaddingX,
                    flexShrink: 0,
                  }),
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: editorTokens.rightRail.headerGap,
              borderBottom: `1px solid ${editorTokens.rightRail.borderColor}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: editorTokens.rightRail.headerGap,
                color: editorTokens.palette.ink,
              }}
            >
              <PropertiesTabIcon size={18} />
              <Typography
                id="field-properties-heading"
                component="h2"
                sx={{
                  fontSize: editorTokens.rightRail.headerFontSize,
                  fontWeight: editorTokens.rightRail.headerFontWeight,
                  color: editorTokens.palette.ink,
                  lineHeight: "normal",
                }}
              >
                Field properties
              </Typography>
            </Box>
            {headerAction && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {headerAction}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              ...(isMobile
                ? { height: "80vh" }
                : { flex: 1, minHeight: 0, alignItems: "stretch" }),
              overflowY: "auto",
              backgroundColor: "neutral.50",
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "signer.border.strong",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "signer.text.muted",
                },
              },
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent={"space-between"}
              px={2}
              pt={2}
              pb={2}
            >
              <Stack direction="row" alignItems="center">
                <Typography component="h3" variant="subtitle2">
                  {/* {capitalizeText(selectedFieldType)} */}
                  {selectedFieldType === "radio_button"
                    ? "Radio Button"
                    : selectedFieldType === "drop_down"
                      ? "Drop Down"
                      : capitalizeText(selectedFieldType)}
                </Typography>
                <Chip
                  label={selectedField.required ? "Required" : "Optional"}
                  size="small"
                  sx={{
                    backgroundColor: (theme) =>
                      selectedField.required
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.09),
                    color: selectedField.required ? "common.white" : "inherit",
                    height: 20,
                    fontSize: "0.7rem",
                    ml: 0.5,
                  }}
                />
              </Stack>
              <Tooltip title="Delete" arrow>
                <IconButton
                  size="small"
                  aria-label="Delete field"
                  onClick={handleDelete}
                  sx={{
                    p: 0.5,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Close" arrow>
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{
                    p: 0.5,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip> */}
            </Stack>
            <Box px={2} pt={2} pb={2}>
              <Typography
                id="signerSelect"
                variant="caption"
                sx={{ color: "text.secondary", mb: 0.5, display: "block" }}
              >
                Assigned to
              </Typography>

              <Select
                labelId="signerSelect"
                id="signerSelectId"
                value={signerOfSelectedField?.id}
                disabled={isBulkSend}
                onChange={(event) => {
                  reAssignSigner(
                    Number(event.target.value) === ANY_SIGNER
                      ? ANY_SIGNER_OPTION
                      : signRequest.signers.find(
                          (signer: RequestParticipant) =>
                            signer.id == event.target.value,
                        )!,
                  );
                }}
                variant="standard"
                input={<OutlinedInput notched={false} />}
                disableUnderline={false}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      width: "10%",
                      minWidth: "100%",
                      maxHeight: 300,
                      mt: 1,
                    },
                  },
                  MenuListProps: {
                    sx: {
                      "& .Mui-selected": {
                        backgroundColor: `${theme.palette.neutral[100]} !important`,
                      },
                    },
                  },
                }}
                sx={{
                  width: "100%",
                  borderRadius: "5px",
                  "& .MuiInputBase-input": {
                    position: "relative",
                    padding: "4px 12px",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "neutral.200",
                    },
                  },
                  "&.MuiOutlinedInput-root": {
                    // ...responsiveFont,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "neutral.200",
                  },
                  backgroundColor: `${signerOfSelectedField?.color}1A !important`,
                  border: `1px solid ${signerOfSelectedField?.color}`,
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  "& .MuiSelect-filled": {
                    padding: "10px",
                  },
                  "& .MuiSelect-icon": {
                    color: signerOfSelectedField?.color,
                  },
                  boxShadow: "none",
                  ".MuiOutlinedInput-notchedOutline": {
                    border: 0,
                  },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                    {
                      border: 0,
                    },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      border: 0,
                    },
                }}
                renderValue={() => {
                  return isTruthy(signerOfSelectedField?.name) ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        mt: "2px",
                      }}
                    >
                      <Box
                        sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: signerOfSelectedField.color,
                        }}
                      ></Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "500",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          marginLeft: 1,
                          "@media (min-width: 1px)": {
                            "&:hover, &:focus": {
                              overflow: "visible",
                              textOverflow: "clip",
                              whiteSpace: "normal",
                              maxWidth: "none",
                              position: "relative",
                              zIndex: 1000,
                              backgroundColor: "background.paper",
                              padding: "4px",
                              borderRadius: "4px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            },
                          },
                        }}
                        className="zoom-friendly-text"
                      >
                        {signerOfSelectedField?.name}
                      </Typography>
                    </Box>
                  ) : (
                    <Stack
                      direction={"row"}
                      justifyContent={"start"}
                      alignItems={"center"}
                      spacing={1}
                    >
                      <Box
                        sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: signerOfSelectedField.color,
                        }}
                      ></Box>
                      <Typography>
                        {signerOfSelectedField?.name
                          ? signerOfSelectedField.name
                          : `Signer ${signerOfSelectedField?.order ?? ""}`}
                      </Typography>
                    </Stack>
                  );
                }}
              >
                {isLibraryPlacedField &&
                  ANY_SIGNER_FIELD_TYPES.includes(selectedFieldType) &&
                  !signRequest.selfSigned && (
                    <MenuItem
                      key={ANY_SIGNER}
                      value={ANY_SIGNER}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: `${ANY_SIGNER_COLOR}1A`,
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: `${ANY_SIGNER_COLOR}33`,
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        width="100%"
                      >
                        <Box
                          sx={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: ANY_SIGNER_COLOR,
                          }}
                        ></Box>
                        <Box sx={{ overflow: "hidden" }}>
                          <Typography fontWeight="bold" fontSize="12px" noWrap>
                            {strings.anySigner}
                          </Typography>
                          <Typography
                            fontSize="12px"
                            sx={{ color: "text.secondary" }}
                            noWrap
                          >
                            Whoever signs first fills it
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  )}
                {signRequest.signers &&
                  signRequest.signers
                    .filter((signer: RequestParticipant) => !signer.cc)
                    .map((signer: RequestParticipant) => (
                      <MenuItem
                        key={signer.id}
                        value={signer.id}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: `${signer.color}1A`,
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: `${signer.color}33`,
                          },
                        }}
                      >
                        {isTruthy(signer.name) ? (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            width="100%"
                          >
                            <Box
                              sx={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                backgroundColor: signer.color,
                              }}
                            ></Box>
                            <Box sx={{ overflow: "hidden" }}>
                              <Typography
                                fontWeight="bold"
                                fontSize="12px"
                                noWrap
                              >
                                {signer.name}
                              </Typography>
                              {signer.email.length > 25 ? (
                                <Tooltip
                                  title={signer.email}
                                  placement="top"
                                  arrow
                                >
                                  <Typography
                                    fontSize="10px"
                                    color="gray"
                                    noWrap
                                  >
                                    {signer.email.slice(0, 25)}...
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography fontSize="12px" color="gray" noWrap>
                                  {signer.email}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        ) : (
                          <Stack
                            direction={"row"}
                            justifyContent={"start"}
                            alignItems={"center"}
                            spacing={1}
                          >
                            <Box
                              sx={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                backgroundColor: signer.color,
                              }}
                            ></Box>
                            <Typography>
                              {signer.name
                                ? signer.name
                                : `Signer ${signer.order}`}
                            </Typography>
                          </Stack>
                        )}
                      </MenuItem>
                    ))}
              </Select>

              <Typography
                id="field-type-label"
                variant="caption"
                sx={{
                  color: "text.secondary",
                  mt: 1,
                  mb: 0.5,
                  display: "block",
                }}
              >
                Type
              </Typography>
              <Select
                fullWidth
                size="small"
                id="change-type-in-selectedField"
                labelId="field-type-label"
                name="type"
                value={selectedFieldType}
                onChange={updateSelectedField}
                sx={{ mb: 1, fontSize: "0.875rem", ...selectFocusSx }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
              >
                {Object.values(FieldsProperties)
                  .flatMap((section) => section.map((item: any) => item.type))
                  .map((type: string, ind: number) => (
                    <MenuItem
                      id={`change-type-menu-item-${type}`}
                      key={ind + type}
                      value={type}
                    >
                      {/* {capitalizeText(type)} */}
                      {type === "radio_button"
                        ? "Radio Button"
                        : type === "drop_down"
                          ? "Drop Down"
                          : capitalizeText(type)}
                    </MenuItem>
                  ))}
              </Select>
              {sourceCustomField && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 1 }}
                >
                  From custom field "{sourceCustomField.name}". Changes here
                  apply to this document only.
                </Typography>
              )}
            </Box>

            {(selectedFieldType === strings.text ||
              selectedFieldType === strings.dropDown ||
              selectedFieldType === strings.email ||
              selectedFieldType === strings.name ||
              selectedFieldType === strings.companyName ||
              selectedFieldType === strings.title) && (
              <FieldCustomAccordion
                currentAccordion={currentAccordion}
                name={strings.formatting}
                accordionSummary={getSummary(strings.formatting)!}
                accordionDetails={getDetails(strings.formatting)!}
                onChange={handleAccordion}
                accordionSummarySx={{
                  paddingX: "10px",
                  backgroundColor: "neutral.50",
                }}
                accordionDetailsSx={{
                  backgroundColor: "neutral.50",
                }}
              />
            )}

            <FieldCustomAccordion
              currentAccordion={currentAccordion}
              name={strings.fieldData}
              accordionSummary={getSummary(strings.fieldData)!}
              accordionDetails={getDetails(strings.fieldData)!}
              onChange={handleAccordion}
              accordionSummarySx={{
                paddingX: "10px",
                backgroundColor: "neutral.50",
              }}
              accordionDetailsSx={{
                backgroundColor: "neutral.50",
              }}
            />
            {/* A library field takes the full validation set; a Fields-panel field keeps the
                original gate, which only showed Validations for a REQUIRED text field. */}
            {(isLibraryPlacedField
              ? PATTERN_FIELD_TYPES.includes(selectedFieldType)
              : selectedFieldType === strings.text && selectedField.required) && (
              <FieldCustomAccordion
                currentAccordion={currentAccordion}
                name={strings.validations}
                accordionSummary={getSummary(strings.validations)!}
                accordionDetails={getDetails(strings.validations)!}
                onChange={handleAccordion}
                accordionSummarySx={{
                  paddingX: "10px",
                  backgroundColor: "neutral.50",
                }}
                accordionDetailsSx={{
                  backgroundColor: "neutral.50",
                }}
              />
            )}
          </Box>
        </Box>
      </Stack>
    </Fade>
  );
};

export default PropertiesDrawer;
